// TaskHubSettings.api.ts

import { v4 as uuid } from 'uuid';
import {
  TaskHubUserSettings,
  TaskHubQuery,
  TaskHubFilters,
} from './TaskHubSettings.types';
import {
  apiFetchSettings,
  apiUpdateSetting,
  apiSaveUserSetting,
} from '../../settings/api';
import {
  Setting,
  SettingKey,
  UpdateSetting,
  CreateSetting,
} from '../../../models/Settings';

const TASKHUB_SETTING_KEY = 'TaskHub_UserSetting';
const MAX_QUERIES_LIMIT = 10;

// Utility functions
export const generateGuid = (): string => {
  return uuid();
};

export const createTaskHubQuery = (
  displayName: string,
  filters: TaskHubFilters
): TaskHubQuery => {
  return {
    timestamp: new Date().toISOString(),
    taskHubQueryId: generateGuid(),
    displayName: displayName,
    plannerIds: filters.plannerIds || [],
    assigneeIds: filters.assigneeIds || [],
    projectIds: filters.projectIds || [],
    priorityIds: filters.priorityIds || [],
    labelIds: filters.labelIds || [],
    statusIds: filters.statusIds || [],
  };
};

const createDefaultSettings = (): TaskHubUserSettings => {
  var generatedGuid = generateGuid();
  return {
    taskHubQueries: [
      {
        timestamp: new Date().toISOString(),
        taskHubQueryId: generatedGuid,
        displayName: 'Default TaskHub',
        assigneeIds: [],
        plannerIds: [],
        projectIds: [],
        priorityIds: [],
        labelIds: [],
        statusIds: [],
      },
    ],
    defaultTaskHubId: generatedGuid,
  };
};

const validateSettings = (settings: TaskHubUserSettings): void => {
  if (!settings.taskHubQueries || !Array.isArray(settings.taskHubQueries)) {
    throw new Error('Invalid settings format: taskHubQueries must be an array');
  }

  if (settings.taskHubQueries.length > MAX_QUERIES_LIMIT) {
    throw new Error(`Maximum ${MAX_QUERIES_LIMIT} TaskHub queries allowed`);
  }

  // Validate each query
  settings.taskHubQueries.forEach((query, index) => {
    if (!query.taskHubQueryId || !query.displayName) {
      throw new Error(
        `Invalid query at index ${index}: missing required fields`
      );
    }
  });
};

export const apiFetchTaskHubUserSettings = async (
  contactId: string
): Promise<{ settings: TaskHubUserSettings; settingId?: string }> => {
  if (!contactId) {
    throw new Error('Contact ID is required');
  }

  try {
    const settingKey: SettingKey = {
      keys: [TASKHUB_SETTING_KEY],
      scopeId: contactId,
      eSettingScope: 'user',
    };

    const { data, result } = await apiFetchSettings([settingKey]);

    if (!result.ok) {
      throw new Error(
        `Failed to fetch settings: ${result.status} ${result.statusText}`
      );
    }

    if (data && Array.isArray(data) && data.length > 0) {
      const setting: Setting = data[0];

      if (setting.jsonValue) {
        const settings: TaskHubUserSettings = JSON.parse(setting.jsonValue);
        validateSettings(settings);
        return { settings, settingId: setting.settingId };
      }
    }

    // Return default settings if none exist
    return { settings: createDefaultSettings() };
  } catch (error) {
    console.error('Error fetching TaskHub user settings:', error);

    // If parsing fails, return default settings
    if (error instanceof SyntaxError) {
      console.warn('Invalid JSON in settings, returning defaults');
      return { settings: createDefaultSettings() };
    }

    throw error;
  }
};

export const apiSaveTaskHubUserSettings = async (
  contactId: string,
  settings: TaskHubUserSettings,
  existingSettingId?: string
): Promise<void> => {
  if (!contactId) {
    throw new Error('Contact ID is required');
  }

  try {
    validateSettings(settings);

    const jsonValue = JSON.stringify(settings);
    const emptyGuid = '00000000-0000-0000-0000-000000000000';

    // Check if we need to create or update
    const shouldCreate = !existingSettingId || existingSettingId === emptyGuid;

    if (shouldCreate) {
      // Create new user setting
      const createSetting: CreateSetting = {
        jsonValue,
        parentSettingId: '',
        relationId: '',
        isDefault: false,
        description: 'TaskHub user settings',
      };

      const { result } = await apiSaveUserSetting(
        contactId,
        TASKHUB_SETTING_KEY,
        createSetting
      );

      if (!result.ok) {
        throw new Error(
          `Failed to create settings: ${result.status} ${result.statusText}`
        );
      }
    } else {
      // Update existing setting
      const updateSetting: UpdateSetting = {
        jsonValue,
        parentSettingId: '',
        relationId: '',
        isDefault: false,
        description: 'TaskHub user settings',
      };

      const { result } = await apiUpdateSetting(
        existingSettingId,
        updateSetting
      );

      if (!result.ok) {
        throw new Error(
          `Failed to update settings: ${result.status} ${result.statusText}`
        );
      }
    }
  } catch (error) {
    console.error('Error saving TaskHub user settings:', error);
    throw error;
  }
};
